'use client';

import { useRef, useState, useEffect } from 'react';
import { ChevronDown, ShieldCheck, Sparkles } from 'lucide-react';
import { useAuth, PlanView } from '@/lib/auth-context';
import clsx from 'clsx';

const PLAN_OPTIONS: { value: PlanView; label: string; icon: React.ElementType; color: string }[] = [
  { value: 'admin', label: 'Admin View', icon: ShieldCheck, color: 'text-[#F59E0B]' },
  { value: 'crewsync' as PlanView, label: 'CrewSync Plan', icon: Sparkles, color: 'text-[#00C2A8]' },
];

export default function PlanToggle() {
  const { isAdmin, viewingPlan, setViewingPlan } = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  if (!isAdmin) return null;

  const current = PLAN_OPTIONS.find(p => p.value === viewingPlan) || PLAN_OPTIONS[0];
  const Icon = current.icon;

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-[--border-primary] bg-[--bg-main] hover:bg-[--bg-card] text-[--text-secondary] hover:text-[--text-primary] transition-colors text-xs font-medium"
        title="Switch plan view"
      >
        <Icon className={clsx('w-3.5 h-3.5', current.color)} />
        <span className="hidden sm:block">{current.label}</span>
        <ChevronDown className="w-3 h-3" />
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1 w-44 bg-[--bg-card] border border-[--border-primary] rounded-xl shadow-2xl z-50 overflow-hidden py-1">
          {PLAN_OPTIONS.map(option => {
            const OptionIcon = option.icon;
            const active = viewingPlan === option.value;
            return (
              <button
                key={option.value}
                onClick={() => { setViewingPlan(option.value); setOpen(false); }}
                className={clsx(
                  'w-full flex items-center gap-2.5 px-3 py-2 text-xs font-medium transition-colors text-left',
                  active
                    ? 'bg-[#00C2A8]/10 text-[#00C2A8]'
                    : 'text-[--text-secondary] hover:bg-[--bg-main] hover:text-[--text-primary]'
                )}
              >
                <OptionIcon className={clsx('w-3.5 h-3.5', active ? 'text-[#00C2A8]' : option.color)} />
                {option.label}
                {active && <span className="ml-auto w-1.5 h-1.5 rounded-full bg-[#00C2A8]" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
